import { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { runBigQueryQuery } from "../bigquery/BigQueryClient";
import { computeXmr, type Xmr } from "../metrics/xmr";
import { computeKalman } from "../metrics/kalman";
import XmrChart from "../components/charts/XmrChart";
import EChart from "../components/charts/EChart";
import { defaultGrid, tooltipCss } from "../components/charts/chartDefaults";
import HeroHeader from "../components/AudienceHeroHeader";
import SectionDivider from "../components/SectionDivider";
import type { Timeseries } from "../types";
import type { EChartsOption } from "echarts";

/* ──────────────────────── Interfaces ──────────────────────── */

interface AudienceSummary {
  audience_name: string;
  max_date: string;
  revenue_eur: number;
  prev_revenue_eur: number;
  impressions: number;
  prev_impressions: number;
  wins: number;
  bids: number;
  prev_wins: number;
  prev_bids: number;
  clicks: number;
  prev_clicks: number;
}

interface DailyD1Row {
  bid_date: string;
  revenue_eur: number;
  d1_ap_conversions: number;
  d1_ap_customer_revenue_eur: number;
  d7_ap_conversions: number;
  d7_ap_customer_revenue_eur: number;
  d30_ap_conversions: number;
  d30_ap_customer_revenue_eur: number;
  supply_cost_eur: number;
}

interface DailyDeliveryRow {
  day: string;
  wins: number;
  bids: number;
  clicks: number;
  impressions: number;
}

interface BudgetRow {
  budget_pool_id: string;
  budget_name: string;
  revenue_eur: number;
  prev_revenue_eur: number;
  d1_ecpx_eur: number | null;
  d1_roas: number | null;
}

interface D1Metrics {
  d1_ecpx_eur: number | null;
  prev_d1_ecpx_eur: number | null;
  d1_roas: number | null;
  prev_d1_roas: number | null;
}

interface Assessment {
  level: "good" | "caution" | "poor";
  metric: string;
  reasons: string[];
}

/* ──────────────────────── Formatters ──────────────────────── */

const formatEUR = (v: number) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);

const formatEURCpx = (v: number) =>
  new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "EUR",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(v);

const formatCount = (v: number) => new Intl.NumberFormat("en-GB").format(Math.round(v));

const formatPct = (v: number) => `${(v * 100).toFixed(1)}%`;

const fmtCpxLog = (v: number) => formatEURCpx(Math.exp(v));
const fmtRoasLog = (v: number) => formatPct(Math.exp(v));
const fmtPctAxis = (v: number) => formatPct(v);

function formatChange(current: number, prev: number, lowerIsBetter = false): { label: string; up: boolean } | null {
  if (!prev || prev <= 0) return null;
  const pct = Math.round(((current - prev) / prev) * 100);
  const isUp = lowerIsBetter ? pct <= 0 : pct >= 0;
  return { label: `${pct >= 0 ? "+" : ""}${pct}%`, up: isUp };
}

function formatDateRange(maxDateStr: string): string {
  const end = new Date(maxDateStr);
  const start = new Date(end);
  start.setDate(start.getDate() - 6);
  const fmt = new Intl.DateTimeFormat("en-GB", { day: "numeric", month: "short", year: "numeric" });
  return `${fmt.format(start)} \u2013 ${fmt.format(end)}`;
}

function addDays(dateStr: string, days: number): string {
  const d = new Date(dateStr + "T00:00:00Z");
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

function getChartMaxDate(): string {
  const d = new Date();
  d.setUTCDate(d.getUTCDate() - 2);
  return d.toISOString().slice(0, 10);
}

/* ──────────────────────── SQL Builders ──────────────────────── */

function buildSummarySQL(id: string): string {
  if (!/^\d+$/.test(id)) throw new Error("Invalid audience ID");
  return `
    WITH latest AS (
      SELECT MAX(day) AS max_date
      FROM \`remerge-dwh.ops.apro_campaign_level_performance\`
      WHERE audience_id = ${id}
        AND day >= DATE_SUB(CURRENT_DATE(), INTERVAL 30 DAY)
        AND day <= DATE_SUB(CURRENT_DATE(), INTERVAL 2 DAY)
    ),
    daily AS (
      SELECT
        day,
        SUM(revenue_eur) AS revenue_eur,
        SUM(impressions) AS impressions,
        SUM(wins) AS wins,
        SUM(bids) AS bids,
        SUM(clicks) AS clicks
      FROM \`remerge-dwh.ops.apro_campaign_level_performance\`
      CROSS JOIN latest l
      WHERE audience_id = ${id}
        AND day >= DATE_SUB(l.max_date, INTERVAL 14 DAY)
      GROUP BY day
    )
    SELECT
      a.name AS audience_name,
      l.max_date,
      SUM(CASE WHEN d.day >= DATE_SUB(l.max_date, INTERVAL 7 DAY) THEN d.revenue_eur ELSE 0 END) AS revenue_eur,
      SUM(CASE WHEN d.day < DATE_SUB(l.max_date, INTERVAL 7 DAY) THEN d.revenue_eur ELSE 0 END) AS prev_revenue_eur,
      SUM(CASE WHEN d.day >= DATE_SUB(l.max_date, INTERVAL 7 DAY) THEN d.impressions ELSE 0 END) AS impressions,
      SUM(CASE WHEN d.day < DATE_SUB(l.max_date, INTERVAL 7 DAY) THEN d.impressions ELSE 0 END) AS prev_impressions,
      SUM(CASE WHEN d.day >= DATE_SUB(l.max_date, INTERVAL 7 DAY) THEN d.wins ELSE 0 END) AS wins,
      SUM(CASE WHEN d.day >= DATE_SUB(l.max_date, INTERVAL 7 DAY) THEN d.bids ELSE 0 END) AS bids,
      SUM(CASE WHEN d.day < DATE_SUB(l.max_date, INTERVAL 7 DAY) THEN d.wins ELSE 0 END) AS prev_wins,
      SUM(CASE WHEN d.day < DATE_SUB(l.max_date, INTERVAL 7 DAY) THEN d.bids ELSE 0 END) AS prev_bids,
      SUM(CASE WHEN d.day >= DATE_SUB(l.max_date, INTERVAL 7 DAY) THEN d.clicks ELSE 0 END) AS clicks,
      SUM(CASE WHEN d.day < DATE_SUB(l.max_date, INTERVAL 7 DAY) THEN d.clicks ELSE 0 END) AS prev_clicks
    FROM daily d
    CROSS JOIN latest l
    CROSS JOIN \`martin-test-datalab.postgres.audiences\` a
    WHERE a.id = ${id}
    GROUP BY a.name, l.max_date
  `;
}

function buildCohortedDailySQL(audienceId: string, maxDate: string): string {
  if (!/^\d+$/.test(audienceId)) throw new Error("Invalid audience ID");
  const start = addDays(maxDate, -27);
  return `
    SELECT
      bid_date,
      SUM(revenue_eur) AS revenue_eur,
      SUM(supply_cost_eur) AS supply_cost_eur,
      SUM(d1_ap_conversions) AS d1_ap_conversions,
      SUM(d1_ap_customer_revenue_eur) AS d1_ap_customer_revenue_eur,
      SUM(d7_ap_conversions) AS d7_ap_conversions,
      SUM(d7_ap_customer_revenue_eur) AS d7_ap_customer_revenue_eur,
      SUM(d30_ap_conversions) AS d30_ap_conversions,
      SUM(d30_ap_customer_revenue_eur) AS d30_ap_customer_revenue_eur
    FROM \`remerge-dwh.ops.apro_campaign_level_cohorted_performance\`
    WHERE audience_id = ${audienceId}
      AND bid_date >= '${start}' AND bid_date <= '${maxDate}'
    GROUP BY bid_date
    ORDER BY bid_date
  `;
}

function buildDeliveryDailySQL(audienceId: string, maxDate: string): string {
  if (!/^\d+$/.test(audienceId)) throw new Error("Invalid audience ID");
  const start = addDays(maxDate, -27);
  return `
    SELECT
      day,
      SUM(wins) AS wins,
      SUM(bids) AS bids,
      SUM(clicks) AS clicks,
      SUM(impressions) AS impressions
    FROM \`remerge-dwh.ops.apro_campaign_level_performance\`
    WHERE audience_id = ${audienceId}
      AND day >= '${start}' AND day <= '${maxDate}'
    GROUP BY day
    ORDER BY day
  `;
}

function buildBudgetsSQL(id: string, maxDate: string): string {
  if (!/^\d+$/.test(id)) throw new Error("Invalid audience ID");
  const split = addDays(maxDate, -6);
  const start = addDays(maxDate, -13);
  return `
    WITH campaign_info AS (
      SELECT DISTINCT c.id AS campaign_id, c.budget_pool_id, bp.name AS budget_name
      FROM \`martin-test-datalab.postgres.campaigns\` c
      JOIN \`martin-test-datalab.postgres.budget_pools\` bp ON c.budget_pool_id = bp.id
      WHERE c.audience_id = ${id}
        AND bp.name NOT LIKE '%(migrated)%'
    ),
    daily_rev AS (
      SELECT
        ci.budget_pool_id,
        ci.budget_name,
        p.day,
        SUM(p.revenue_eur) AS revenue_eur
      FROM \`remerge-dwh.ops.apro_campaign_level_performance\` p
      JOIN campaign_info ci ON p.campaign_id = ci.campaign_id
      WHERE p.day >= '${start}' AND p.day <= '${maxDate}'
      GROUP BY ci.budget_pool_id, ci.budget_name, p.day
    ),
    cohorted AS (
      SELECT
        ci.budget_pool_id,
        SUM(CASE WHEN c.bid_date >= '${split}' THEN c.revenue_eur ELSE 0 END) AS curr_revenue,
        SUM(CASE WHEN c.bid_date >= '${split}' THEN c.d1_ap_conversions ELSE 0 END) AS curr_d1_conv,
        SUM(CASE WHEN c.bid_date >= '${split}' THEN c.d1_ap_customer_revenue_eur ELSE 0 END) AS curr_ap_rev
      FROM \`remerge-dwh.ops.apro_campaign_level_cohorted_performance\` c
      JOIN campaign_info ci ON c.campaign_id = ci.campaign_id
      WHERE c.bid_date >= '${start}' AND c.bid_date <= '${maxDate}'
      GROUP BY ci.budget_pool_id
    )
    SELECT
      CAST(r.budget_pool_id AS STRING) AS budget_pool_id,
      r.budget_name,
      SUM(CASE WHEN r.day >= '${split}' THEN r.revenue_eur ELSE 0 END) AS revenue_eur,
      SUM(CASE WHEN r.day < '${split}' THEN r.revenue_eur ELSE 0 END) AS prev_revenue_eur,
      SAFE_DIVIDE(MAX(co.curr_revenue), MAX(co.curr_d1_conv)) AS d1_ecpx_eur,
      SAFE_DIVIDE(MAX(co.curr_ap_rev), MAX(co.curr_revenue)) AS d1_roas
    FROM daily_rev r
    LEFT JOIN cohorted co ON r.budget_pool_id = co.budget_pool_id
    GROUP BY r.budget_pool_id, r.budget_name
    HAVING SUM(CASE WHEN r.day >= '${split}' THEN r.revenue_eur ELSE 0 END) > 0
    ORDER BY revenue_eur DESC
  `;
}

/* ──────────────────────── Data Transforms ──────────────────────── */

function computeD1FromDaily(rows: DailyD1Row[], maxDate: string): D1Metrics {
  const split = addDays(maxDate, -6);
  let cr = 0,
    pr = 0,
    cc = 0,
    pc = 0,
    car = 0,
    par = 0;
  for (const row of rows) {
    if (row.bid_date >= split) {
      cr += row.revenue_eur;
      cc += row.d1_ap_conversions;
      car += row.d1_ap_customer_revenue_eur;
    } else {
      pr += row.revenue_eur;
      pc += row.d1_ap_conversions;
      par += row.d1_ap_customer_revenue_eur;
    }
  }
  return {
    d1_ecpx_eur: cc > 0 ? cr / cc : null,
    prev_d1_ecpx_eur: pc > 0 ? pr / pc : null,
    d1_roas: cr > 0 ? car / cr : null,
    prev_d1_roas: pr > 0 ? par / pr : null,
  };
}

function buildCohortedTimeseries(rows: DailyD1Row[]): {
  revenue: Timeseries;
  ecpx: Timeseries;
  roas: Timeseries;
  d7Ecpx: Timeseries;
  d7Roas: Timeseries;
  d30Ecpx: Timeseries;
  d30Roas: Timeseries;
} {
  const timestamps = rows.map(r => {
    const [year, month, day] = r.bid_date.split("-").map(Number);
    return new Date(year, month - 1, day);
  });
  return {
    revenue: { timestamps, data: rows.map(r => r.revenue_eur) },
    ecpx: {
      timestamps,
      data: rows.map(r => (r.d1_ap_conversions > 0 ? r.revenue_eur / r.d1_ap_conversions : null)),
    },
    roas: {
      timestamps,
      data: rows.map(r =>
        r.revenue_eur > 0 && r.d1_ap_customer_revenue_eur > 0 ? r.d1_ap_customer_revenue_eur / r.revenue_eur : null,
      ),
    },
    d7Ecpx: {
      timestamps,
      data: rows.map(r => (r.d7_ap_conversions > 0 ? r.revenue_eur / r.d7_ap_conversions : null)),
    },
    d7Roas: {
      timestamps,
      data: rows.map(r =>
        r.revenue_eur > 0 && r.d7_ap_customer_revenue_eur > 0 ? r.d7_ap_customer_revenue_eur / r.revenue_eur : null,
      ),
    },
    d30Ecpx: {
      timestamps,
      data: rows.map(r => (r.d30_ap_conversions > 0 ? r.revenue_eur / r.d30_ap_conversions : null)),
    },
    d30Roas: {
      timestamps,
      data: rows.map(r =>
        r.revenue_eur > 0 && r.d30_ap_customer_revenue_eur > 0 ? r.d30_ap_customer_revenue_eur / r.revenue_eur : null,
      ),
    },
  };
}

function buildDeliveryTimeseries(rows: DailyDeliveryRow[]): {
  winRate: Timeseries;
  ctr: Timeseries;
} {
  const timestamps = rows.map(r => {
    const [year, month, day] = r.day.split("-").map(Number);
    return new Date(year, month - 1, day);
  });
  return {
    winRate: {
      timestamps,
      data: rows.map(r => (r.bids > 0 ? r.wins / r.bids : null)),
    },
    ctr: {
      timestamps,
      data: rows.map(r => (r.impressions > 0 ? r.clicks / r.impressions : null)),
    },
  };
}

/* ──────────────────────── Health Assessment ──────────────────────── */

function assessHealth(xmrRevenue: Xmr | null, xmrEcpx: Xmr | null, xmrRoas: Xmr | null): Assessment[] {
  const assessments: Assessment[] = [];

  if (xmrRevenue) {
    const count = xmrRevenue.anomalies.length;
    const level = count === 0 ? "good" : count <= 2 ? "caution" : "poor";
    const reasons: string[] = [];
    if (count === 0) {
      reasons.push("Revenue is within natural process limits over the 28-day window. No anomalies detected.");
    } else {
      reasons.push(`${count} anomal${count === 1 ? "y" : "ies"} detected in the revenue timeseries.`);
      if (count >= 3) reasons.push("The process may have shifted. Investigate recent changes to campaigns or budgets.");
    }
    assessments.push({ level, metric: "Revenue Stability", reasons });
  }

  if (xmrEcpx) {
    const count = xmrEcpx.anomalies.length;
    const level = count === 0 ? "good" : count <= 2 ? "caution" : "poor";
    const reasons: string[] = [];
    if (count === 0) {
      reasons.push("D1 eCPX is stable. Conversion efficiency is consistent.");
    } else {
      reasons.push(`${count} anomal${count === 1 ? "y" : "ies"} in D1 eCPX. Cost per conversion has been volatile.`);
    }
    assessments.push({ level, metric: "Conversion Efficiency", reasons });
  }

  if (xmrRoas) {
    const count = xmrRoas.anomalies.length;
    const level = count === 0 ? "good" : count <= 2 ? "caution" : "poor";
    const reasons: string[] = [];
    if (count === 0) {
      reasons.push("D1 AP ROAS is stable within process limits.");
    } else {
      reasons.push(`${count} anomal${count === 1 ? "y" : "ies"} in ROAS. Return on ad spend has been inconsistent.`);
    }
    assessments.push({ level, metric: "ROAS Health", reasons });
  }

  return assessments;
}

/* ──────────────────────── Component ──────────────────────── */

export default function AudienceDetailPage() {
  const { id } = useParams<{ id: string }>();
  const { isAuthenticated, requiresAuth } = useAuth();

  const [summary, setSummary] = useState<AudienceSummary | null>(null);
  const [d1, setD1] = useState<D1Metrics | null>(null);
  const [cohortedRows, setCohortedRows] = useState<DailyD1Row[]>([]);
  const [deliveryRows, setDeliveryRows] = useState<DailyDeliveryRow[]>([]);
  const [budgets, setBudgets] = useState<BudgetRow[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    if (requiresAuth && !isAuthenticated) return;
    setIsLoading(true);
    setError(null);

    let summarySQL: string;
    try {
      summarySQL = buildSummarySQL(id);
    } catch (e) {
      setError(String(e));
      setIsLoading(false);
      return;
    }

    runBigQueryQuery(summarySQL)
      .then(summaryResults => {
        const s = (summaryResults[0] as unknown as AudienceSummary) ?? null;
        setSummary(s);
        if (!s?.max_date) return;

        const chartMaxDate = getChartMaxDate();

        // Run parallel queries once we have max_date
        return Promise.all([
          runBigQueryQuery(buildCohortedDailySQL(id, chartMaxDate)),
          runBigQueryQuery(buildDeliveryDailySQL(id, chartMaxDate)),
          runBigQueryQuery(buildBudgetsSQL(id, s.max_date)),
        ]).then(([cohortedRes, deliveryRes, budgetRes]) => {
          const rows = cohortedRes as unknown as DailyD1Row[];
          setCohortedRows(rows);
          setD1(computeD1FromDaily(rows, chartMaxDate));
          setDeliveryRows(deliveryRes as unknown as DailyDeliveryRow[]);
          setBudgets(budgetRes as unknown as BudgetRow[]);
        });
      })
      .catch(err => setError(String(err)))
      .finally(() => setIsLoading(false));
  }, [id, isAuthenticated, requiresAuth]);

  // XMR computations for cohorted data
  const cohortedTs = useMemo(
    () => (cohortedRows.length > 0 ? buildCohortedTimeseries(cohortedRows) : null),
    [cohortedRows],
  );
  const xmrRevenue = useMemo(() => (cohortedTs ? computeXmr(cohortedTs.revenue) : null), [cohortedTs]);
  const xmrEcpx = useMemo(() => (cohortedTs ? computeXmr(cohortedTs.ecpx, { transform: "log" }) : null), [cohortedTs]);
  const xmrRoas = useMemo(() => {
    if (!cohortedTs) return null;
    const nonNull = cohortedTs.roas.data.filter(v => v != null);
    if (nonNull.length < 3) return null;
    return computeXmr(cohortedTs.roas, { transform: "log" });
  }, [cohortedTs]);
  const kalmanEcpx = useMemo(() => {
    if (!xmrEcpx) return null;
    return computeKalman(xmrEcpx.ts, { Q: 0.1 * xmrEcpx.sigma ** 2, R: xmrEcpx.sigma ** 2 });
  }, [xmrEcpx]);
  const kalmanRoas = useMemo(() => {
    if (!xmrRoas) return null;
    return computeKalman(xmrRoas.ts, { Q: 0.1 * xmrRoas.sigma ** 2, R: xmrRoas.sigma ** 2 });
  }, [xmrRoas]);

  // Delivery timeseries and XMR
  const deliveryTs = useMemo(
    () => (deliveryRows.length > 0 ? buildDeliveryTimeseries(deliveryRows) : null),
    [deliveryRows],
  );
  const xmrWinRate = useMemo(() => {
    if (!deliveryTs) return null;
    const nonNull = deliveryTs.winRate.data.filter(v => v != null);
    if (nonNull.length < 3) return null;
    return computeXmr(deliveryTs.winRate);
  }, [deliveryTs]);
  const xmrCtr = useMemo(() => {
    if (!deliveryTs) return null;
    const nonNull = deliveryTs.ctr.data.filter(v => v != null);
    if (nonNull.length < 3) return null;
    return computeXmr(deliveryTs.ctr);
  }, [deliveryTs]);
  const kalmanWinRate = useMemo(() => {
    if (!xmrWinRate) return null;
    return computeKalman(xmrWinRate.ts, { Q: 0.1 * xmrWinRate.sigma ** 2, R: xmrWinRate.sigma ** 2 });
  }, [xmrWinRate]);
  const kalmanCtr = useMemo(() => {
    if (!xmrCtr) return null;
    return computeKalman(xmrCtr.ts, { Q: 0.1 * xmrCtr.sigma ** 2, R: xmrCtr.sigma ** 2 });
  }, [xmrCtr]);

  // Cohort comparison chart options
  const cohortCutoffs = useMemo(() => {
    const maxDate = getChartMaxDate();
    const d7Cut = new Date(addDays(maxDate, -7) + "T00:00:00").getTime();
    const d30Cut = new Date(addDays(maxDate, -30) + "T00:00:00").getTime();
    return { d7Cut, d30Cut };
  }, []);

  const cohortMarkLine = useMemo(
    () => ({
      silent: true,
      symbol: "none",
      label: { fontSize: 9, color: "#8C8B7C" },
      data: [
        {
          xAxis: cohortCutoffs.d7Cut,
          label: { formatter: "D7 full", position: "insideEndTop" },
          lineStyle: { color: "#D2C8FF", type: "dotted" as const, width: 1 },
        },
        {
          xAxis: cohortCutoffs.d30Cut,
          label: { formatter: "D30 full", position: "insideEndTop" },
          lineStyle: { color: "#BFBDA7", type: "dotted" as const, width: 1 },
        },
      ],
    }),
    [cohortCutoffs],
  );

  const cohortEcpxOption = useMemo<EChartsOption | null>(() => {
    if (!cohortedTs) return null;
    const ts = cohortedTs.ecpx.timestamps.map(t => t.getTime());
    return {
      animation: false,
      tooltip: { trigger: "axis", extraCssText: tooltipCss, valueFormatter: (v: unknown) => formatEURCpx(Number(v)) },
      legend: { data: ["D1", "D7", "D30"] },
      grid: defaultGrid,
      xAxis: { type: "time", boundaryGap: false } as never,
      yAxis: { type: "value", axisLabel: { formatter: formatEURCpx } },
      series: [
        {
          name: "D1",
          type: "line",
          data: ts.map((t, i) => [t, cohortedTs.ecpx.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#1D1D1F" },
          itemStyle: { color: "#1D1D1F" },
          markLine: cohortMarkLine,
        } as never,
        {
          name: "D7",
          type: "line",
          data: ts.map((t, i) => [t, cohortedTs.d7Ecpx.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#D2C8FF" },
          itemStyle: { color: "#D2C8FF" },
        },
        {
          name: "D30",
          type: "line",
          data: ts.map((t, i) => [t, cohortedTs.d30Ecpx.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#BFBDA7" },
          itemStyle: { color: "#BFBDA7" },
        },
      ],
    };
  }, [cohortedTs, cohortMarkLine]);

  const cohortRoasOption = useMemo<EChartsOption | null>(() => {
    if (!cohortedTs) return null;
    const ts = cohortedTs.roas.timestamps.map(t => t.getTime());
    return {
      animation: false,
      tooltip: { trigger: "axis", extraCssText: tooltipCss, valueFormatter: (v: unknown) => formatPct(Number(v)) },
      legend: { data: ["D1", "D7", "D30"] },
      grid: defaultGrid,
      xAxis: { type: "time", boundaryGap: false } as never,
      yAxis: { type: "value", axisLabel: { formatter: (v: number) => formatPct(v) } },
      series: [
        {
          name: "D1",
          type: "line",
          data: ts.map((t, i) => [t, cohortedTs.roas.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#1D1D1F" },
          itemStyle: { color: "#1D1D1F" },
          markLine: cohortMarkLine,
        } as never,
        {
          name: "D7",
          type: "line",
          data: ts.map((t, i) => [t, cohortedTs.d7Roas.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#D2C8FF" },
          itemStyle: { color: "#D2C8FF" },
        },
        {
          name: "D30",
          type: "line",
          data: ts.map((t, i) => [t, cohortedTs.d30Roas.data[i]]),
          showSymbol: false,
          lineStyle: { color: "#BFBDA7" },
          itemStyle: { color: "#BFBDA7" },
        },
      ],
    };
  }, [cohortedTs, cohortMarkLine]);

  // Health assessment
  const assessments = useMemo(() => assessHealth(xmrRevenue, xmrEcpx, xmrRoas), [xmrRevenue, xmrEcpx, xmrRoas]);

  // Derived KPI values
  const winRate = summary && summary.bids > 0 ? summary.wins / summary.bids : null;
  const prevWinRate = summary && summary.prev_bids > 0 ? summary.prev_wins / summary.prev_bids : null;
  const dateRange = summary?.max_date ? formatDateRange(summary.max_date) : null;

  if (isLoading) return <div className="entity-list__loading">Loading…</div>;
  if (error)
    return (
      <div className="entity-list__error">
        <p>Something went wrong loading this audience. Please try refreshing the page.</p>
        <p className="entity-list__error-detail">{error}</p>
      </div>
    );

  return (
    <div className="entity-detail audience-report">
      <div className="audience-report__topbar">
        <Link to="/audiences" className="entity-detail__back">
          ← Audiences
        </Link>
        <button className="audience-report__pdf-btn" onClick={() => window.print()}>
          Export PDF
        </button>
      </div>

      {/* ── Section 1: Hero Header ── */}
      <HeroHeader eyebrow="Audience Report" name={summary?.audience_name ?? `Audience ${id}`} dateRange={dateRange} />

      {/* ── Section 2: KPI Strip ── */}
      {(summary || d1) && (
        <div className="audience-kpi-strip">
          {summary && (
            <KpiItem
              label="Revenue"
              value={formatEUR(summary.revenue_eur)}
              change={formatChange(summary.revenue_eur, summary.prev_revenue_eur)}
            />
          )}
          {d1?.d1_ecpx_eur != null && (
            <KpiItem
              label="D1 AP eCPX"
              value={formatEURCpx(d1.d1_ecpx_eur)}
              change={formatChange(d1.d1_ecpx_eur, d1.prev_d1_ecpx_eur ?? 0, true)}
            />
          )}
          {d1?.d1_roas != null && (
            <KpiItem
              label="D1 AP ROAS"
              value={formatPct(d1.d1_roas)}
              change={formatChange(d1.d1_roas, d1.prev_d1_roas ?? 0)}
            />
          )}
          {summary && (
            <KpiItem
              label="Impressions"
              value={formatCount(summary.impressions)}
              change={formatChange(summary.impressions, summary.prev_impressions)}
            />
          )}
          {winRate != null && (
            <KpiItem
              label="Win Rate"
              value={formatPct(winRate)}
              change={prevWinRate ? formatChange(winRate, prevWinRate) : null}
            />
          )}
          {summary && summary.impressions > 0 && summary.clicks > 0 && (
            <KpiItem
              label="CTR"
              value={formatPct(summary.clicks / summary.impressions)}
              change={
                summary.prev_impressions > 0 && summary.prev_clicks > 0
                  ? formatChange(summary.clicks / summary.impressions, summary.prev_clicks / summary.prev_impressions)
                  : null
              }
            />
          )}
        </div>
      )}

      {/* ── Section 3: Health Assessment ── */}
      {assessments.length > 0 && (
        <div className="entity-detail__card">
          <p className="entity-detail__card-label">Health Assessment</p>
          <div className="entity-detail__assessment-grid">
            {assessments.map(a => (
              <div
                key={a.metric}
                className={`entity-detail__assessment-card entity-detail__assessment-card--${a.level}`}
              >
                <div className="entity-detail__assessment-metric">{a.metric}</div>
                <div className="entity-detail__assessment-level">{a.level.toUpperCase()}</div>
                <ul className="entity-detail__assessment-reasons">
                  {a.reasons.map((r, i) => (
                    <li key={i}>{r}</li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Section 4: Process Behavior Charts ── */}
      {(xmrRevenue || xmrEcpx || xmrRoas) && (
        <div className="entity-detail__card audience-section--pagebreak">
          <p className="entity-detail__card-label">Process Behavior — 28 days</p>
          {xmrRevenue && (
            <div className="entity-detail__chart-section">
              <h2 className="entity-detail__chart-title">Revenue (EUR)</h2>
              <XmrChart xmr={xmrRevenue} axisFormatter={formatEUR} />
            </div>
          )}
          {(xmrEcpx || xmrRoas) && (
            <div className="entity-detail__chart-pair">
              {xmrEcpx && (
                <div className="entity-detail__chart-pair-item">
                  <XmrChart
                    xmr={xmrEcpx}
                    kalman={kalmanEcpx ?? undefined}
                    title="D1 AP eCPX"
                    axisFormatter={fmtCpxLog}
                  />
                </div>
              )}
              {xmrRoas && (
                <div className="entity-detail__chart-pair-item">
                  <XmrChart
                    xmr={xmrRoas}
                    kalman={kalmanRoas ?? undefined}
                    title="D1 AP ROAS"
                    axisFormatter={fmtRoasLog}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* ── Cohorted Performance Curves ── */}
      {(cohortEcpxOption || cohortRoasOption) && (
        <div className="entity-detail__card audience-section--pagebreak">
          <p className="entity-detail__card-label">Cohorted Performance — D1 / D7 / D30</p>
          <div className="entity-detail__chart-pair">
            {cohortEcpxOption && (
              <div className="entity-detail__chart-pair-item">
                <h2 className="entity-detail__chart-title">eCPX by Cohort Window</h2>
                <EChart option={cohortEcpxOption} height={350} />
              </div>
            )}
            {cohortRoasOption && (
              <div className="entity-detail__chart-pair-item">
                <h2 className="entity-detail__chart-title">ROAS by Cohort Window</h2>
                <EChart option={cohortRoasOption} height={350} />
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Section 5: Delivery Efficiency ── */}
      {(xmrWinRate || xmrCtr) && (
        <div className="entity-detail__card audience-section--pagebreak">
          <p className="entity-detail__card-label">Delivery Efficiency — 28 days</p>
          <div className="entity-detail__chart-pair">
            {xmrWinRate && (
              <div className="entity-detail__chart-pair-item">
                <XmrChart
                  xmr={xmrWinRate}
                  kalman={kalmanWinRate ?? undefined}
                  title="Win Rate"
                  axisFormatter={fmtPctAxis}
                />
              </div>
            )}
            {xmrCtr && (
              <div className="entity-detail__chart-pair-item">
                <XmrChart xmr={xmrCtr} kalman={kalmanCtr ?? undefined} title="CTR" axisFormatter={fmtPctAxis} />
              </div>
            )}
          </div>
        </div>
      )}

      <SectionDivider />

      {/* ── Section 7: Associated Budgets ── */}
      {budgets.length > 0 && (
        <div className="entity-detail__card audience-section--pagebreak">
          <p className="entity-detail__card-label">Associated Budgets</p>
          <table className="audience-budget-table">
            <thead>
              <tr>
                <th className="audience-budget-table__th">Budget</th>
                <th className="audience-budget-table__th audience-budget-table__th--right">Revenue 7d</th>
                <th className="audience-budget-table__th audience-budget-table__th--right">Change</th>
                <th className="audience-budget-table__th audience-budget-table__th--right">D1 eCPX</th>
                <th className="audience-budget-table__th audience-budget-table__th--right">D1 ROAS</th>
              </tr>
            </thead>
            <tbody>
              {budgets.map(b => {
                const bChange = formatChange(b.revenue_eur, b.prev_revenue_eur);
                return (
                  <tr key={b.budget_pool_id} className="audience-budget-table__row">
                    <td className="audience-budget-table__td">
                      <Link to={`/budgets/${b.budget_pool_id}`} className="entity-list__link">
                        {b.budget_name}
                      </Link>
                    </td>
                    <td className="audience-budget-table__td audience-budget-table__td--right audience-budget-table__td--mono">
                      {formatEUR(b.revenue_eur)}
                    </td>
                    <td className="audience-budget-table__td audience-budget-table__td--right">
                      {bChange && (
                        <span className={`entity-list__change entity-list__change--${bChange.up ? "up" : "down"}`}>
                          {bChange.label}
                        </span>
                      )}
                    </td>
                    <td className="audience-budget-table__td audience-budget-table__td--right audience-budget-table__td--mono">
                      {b.d1_ecpx_eur != null ? formatEURCpx(b.d1_ecpx_eur) : "\u2014"}
                    </td>
                    <td className="audience-budget-table__td audience-budget-table__td--right audience-budget-table__td--mono">
                      {b.d1_roas != null ? formatPct(b.d1_roas) : "\u2014"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Print-only footer */}
      <div className="audience-print-footer">
        {summary?.audience_name} — Audience Report — {dateRange}
      </div>
    </div>
  );
}

/* ──────────────────────── KPI Item (inline) ──────────────────────── */

function KpiItem({
  label,
  value,
  change,
}: {
  label: string;
  value: string;
  change: { label: string; up: boolean } | null;
}) {
  return (
    <div className="audience-kpi-strip__item">
      <div className="entity-detail__kpi-label">{label}</div>
      <div className="entity-detail__kpi-value">{value}</div>
      {change && (
        <div className="entity-detail__kpi-change">
          <span className={`entity-list__change entity-list__change--${change.up ? "up" : "down"}`}>
            {change.label} vs prev 7d
          </span>
        </div>
      )}
    </div>
  );
}
